import { Injectable } from '@angular/core';
import { CacheService } from 'projects/common/src/app/services';
import { IAuthentication, IExposeAuthentication, ISession } from 'projects/common/src/app/interfaces/authentication';
import { Router } from '@angular/router';
import { BusyState } from 'projects/common/src/app/classes';
import { TokenService } from './parameters';
import { Subject, Observable } from 'rxjs';
import { flatMap } from 'rxjs/operators';
import { HttpRequest } from '@angular/common/http';
import { ITokenRefresh } from 'projects/common/src/app/interfaces/remote-models';
import { IRawSession } from 'projects/common/src/app/interfaces/remote-models/raw-session';
import { CommunicationSecurityService } from 'src/app/services/communication';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends BusyState implements IAuthentication<ISession>, IExposeAuthentication<ISession> {
  private refreshTokenPath: string;
  private tokenRefreshSource: Subject<ISession>;

  // Notifys the application when the token expired then modals must be closed
  private tokenExpiredSource = new Subject<void>();
  public tokenExpired$: Observable<void> = this.tokenExpiredSource.asObservable();

  constructor(
    private cacheService: CacheService,
    private tokenService: TokenService,
    private router: Router,
    private http: CommunicationSecurityService,
    private urlBuilder: UrlBuilderService,
  ) {
    super();
    this.tokenRefreshSource = new Subject();
    this.refreshTokenPath = `${this.urlBuilder.securityBasePath}/account/refreshtoken`;
    // Suscripcion del parámetro token a los parámetros de la aplicación
  }

  /**
   * Returns han observable object from ``session token parameter`` source.
   *
   * Emits when the token value has change.
   */
  public onTokenRefresh(): Observable<ISession> {
    return this.tokenRefreshSource.asObservable();
  }

  /**
   * Returns the session id if it exists
   */
  public sessionId(): number {
    if (this.isAuth()) {
      const session = this.getSession();
      // SEssion
      return session.userId;
    }
    return null;
  }

  /**
   * Retorna 'true' si la sesión contiene un valor de token válido.
   *
   * Este método no valida que el token se encuentre vigente en el servidor.
   */
  public isAuth(): boolean {
    const session = this.getSession();
    const result = session && typeof session.token === 'string' && session.token !== 'undefined';
    return !!result;
  }

  /**
   * Elimina la sesión actual.
   *
   * Limpia la sesión del caché y del objeto sesión y redirige al home.
   * Retornará una promesa que se resolverá cuando los procedimientos anteriores
   * se cumplan.
   */
  public cleanSession(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        this.cacheService.clearSession('token');
        this.cacheService.clearSession('selectedMenu');
        this.tokenService.set(null);
        this.tokenExpiredSource.next(); //  ⬅️ Notifys the application that the token expired
        this.router.navigate(['/']);
        resolve(true);
      } catch (error) {
        this.tokenExpiredSource.next(); //  ⬅️ Notifys the application that the token expired when error occurs
        this.cacheService.clearSession('selectedMenu');
        resolve(false);
      }
    });
  }

  /**
   * Elimina la sesión actual y envía un código de error.
   *
   * Limpia la sesión del caché y del objeto sesión y redirige al home.
   * Retornará una promesa que se resolverá cuando los procedimientos anteriores
   * se cumplan.
   */
  public cleanSessionCode(code: string, params?: any): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        this.cacheService.saveSession('errorCode', code);
        this.cacheService.saveSession('errorParam', params || null);
        this.router.navigate(['/app/limbo']);
        resolve(true);
      } catch (error) {
        resolve(false);
      }
    });
  }

  /**
   * Registra un objeto de sesión.
   *
   * Retorna una promesa que se resolverá cuando la sesión sea registrada.
   */
  public registerSession(value: IRawSession): Promise<boolean> {
    return new Promise((resolve) => {
      if (value) {
        // TODO crear provider para esto
        const session: ISession = {
          userId: value.refreshToken.userId,
          sessionId: value.refreshToken.id,
          token: value.token,
          refreshToken: value.refreshToken.refreshToken,
          refreshObject: value.refreshToken,
          user: {
            id: value.userInfo.userId,
            firstName: value.userInfo.firstName,
            lastName: value.userInfo.lastName,
            loginName: value.userInfo.loginName,
            rut: value.userInfo.rut,
            dv: value.userInfo.dv,
            code: value.userInfo.code,
            isActive: value.userInfo.isActive,
            company: {
              id: value.userInfo.companyId,
              name: value.userInfo.companyName,
              enabled: value.userInfo.companyIsActive,
            },
            position: value.userInfo.position,
            positionIsActive: value.userInfo.positionIsActive,
          },
          permissions: value.userPermissions,
        };
        this.tokenService.set(session);
        this.tokenRefreshSource.next(session);
        resolve(true);
      } else {
        resolve(false);
      }
    });
  }

  /**
   * Request for a new ``session token`` and stored it.
   *
   * Returns an Observable object that will emit when the ``session token``
   * has been requested succesfully and the token has been stored. Across all
   * this steps the busy state of the service will be ``true`.
   */
  public refreshToken(): Observable<any> {
    this.setBusyState(true);
    return this.http.post(this.refreshTokenPath, this.getTokenObject()).pipe(
      flatMap((value) => {
        return this.registerSession(value).then(() => {
          this.setBusyState(false);
        });
      }),
    );
  }

  /**
   * Authorize the request with the current ``session token``
   *
   * @param request target request
   */
  public authorizeRequest(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${this.getToken()}` },
    });
  }

  /**
   * Retrurns the object of the current ``token``
   */
  public getSession(): ISession {
    const sessionObject = this.tokenService.get();
    if (sessionObject && typeof sessionObject.token === 'string') {
      return sessionObject;
    }
    return null;
  }

  /**
   * Returns the string value of the current ``token`` object
   */
  public getToken(): string {
    const session = this.getSession();
    if (session) {
      return session.token;
    }
    return null;
  }

  /**
   * Returns the object of the current ``refresh token``
   */
  public getTokenObject(): ITokenRefresh {
    const session = this.getSession();
    if (session) {
      return session.refreshObject;
    }
    return null;
  }

  /**
   * Returns the string value of the current ``refresh token`` if exist
   */
  public getRefreshToken(): string {
    const session = this.getSession();
    if (session) {
      return session.refreshToken;
    }
    return null;
  }

  /**
   * Alias of ``isBusy``
   *
   * Returns ``true`` if the service is currently refreshing the token session.
   */
  public isRefreshingToken = () => this.isBusy();
}
